import * as React from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const GENERAL_QUESTIONS = [
  {
    id: 'panel1',
    q: 'Is Cynthia Data HIPAA-compliant?',
    a: 'Yes. Cynthia generates entirely synthetic data — no real patient records are ever used, stored, or transmitted. All generated data is statistically realistic but contains no actual PHI. This makes it safe to use for model training, development, and testing under HIPAA.',
  },
  {
    id: 'panel2',
    q: 'How does schema upload work?',
    a: 'You can upload a CSV schema, JSON schema, or connect directly to your database. Cynthia analyzes the field names, data types, and value distributions, then generates synthetic data that mirrors your production structure — without ever requiring access to real records.',
  },
  {
    id: 'panel3',
    q: 'What export formats do you support?',
    a: null, // rendered separately to include a Link
  },
  {
    id: 'panel4',
    q: 'Can I try Cynthia before paying?',
    a: 'Absolutely. Our Free plan is free forever with up to 5 cloud records per month and unlimited local generation via the Claude Code plugin — no credit card required. You can upgrade to Credits or Pro when you need higher cloud volumes or direct pipeline export.',
  },
];

export const LS_QUESTIONS = [
  {
    id: 'ls1',
    q: 'Is the generated data CDISC SDTM-compliant?',
    a: 'Yes. Cynthia produces SDTM 3.4-aligned domain files (DM, AE, LB, VS, EX, DS) with correct variable names, controlled terminology, and ISO 8601 date formats. The output is designed to load directly into your EDC or SAS submission pipeline.',
  },
  {
    id: 'ls2',
    q: 'Which therapeutic areas are supported?',
    a: 'Seven therapeutic areas are available out of the box: Oncology, Cardiovascular, CNS, Immunology, Infectious Disease, Rare Disease, and Metabolic. Each uses area-specific MedDRA AE profiles, lab panels, and realistic disposition rates. Additional therapeutic areas are available for Enterprise plans.',
  },
  {
    id: 'ls3',
    q: 'Can I configure custom trial protocols?',
    a: 'Yes. You specify the phase (I–IV), treatment arms (parallel, dose-escalation, crossover), visit schedule, and primary endpoint. Cynthia uses your configuration to produce subjects that reflect realistic enrollment patterns, dropout rates, and arm-specific AE profiles.',
  },
  {
    id: 'ls4',
    q: 'Is this data suitable for EDC load testing and QA?',
    a: 'Exactly what it was built for. The REST API lets you generate and stream subjects directly into your QA environment on every deploy, so your EDC validation suites always run against realistic, protocol-consistent data — without waiting on a data management team.',
  },
  {
    id: 'ls5',
    q: 'Do I need an IRB waiver or BAA to use Cynthia?',
    a: 'No. Because Cynthia generates purely synthetic data with no real patient records involved at any stage, there is no PHI, no covered entity relationship, and no BAA required. Use it freely in development, staging, training, and vendor demonstrations.',
  },
];

export default function FAQ({ questions, title = 'Frequently asked questions', id = 'faq' }) {
  const [expanded, setExpanded] = React.useState(false);
  const items = questions || GENERAL_QUESTIONS;

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Container
      id={id}
      sx={{
        pt: { xs: 4, sm: 12 },
        pb: { xs: 8, sm: 16 },
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: { xs: 3, sm: 6 },
      }}
    >
      <Typography
        component="h2"
        variant="h4"
        color="text.primary"
        sx={{ width: { sm: '100%', md: '60%' }, textAlign: { sm: 'left', md: 'center' } }}
      >
        {title}
      </Typography>
      <Box sx={{ width: '100%' }}>
        {items.map((item) => (
          <Accordion
            key={item.id}
            expanded={expanded === item.id}
            onChange={handleChange(item.id)}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={`${item.id}-content`} id={`${item.id}-header`}>
              <Typography component="h3" variant="subtitle2">{item.q}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {item.id === 'panel3' ? (
                <Typography variant="body2" gutterBottom sx={{ maxWidth: { sm: '100%', md: '70%' } }}>
                  Cynthia exports to CSV, JSON, and FHIR R4 format for healthcare use cases. Pro and Enterprise plans
                  support direct pipeline export to S3, databases, and data warehouses. We&apos;re actively adding
                  more connectors — reach out to{' '}
                  <Link href="mailto:support@bacclabs.io">support@bacclabs.io</Link> for requests.
                </Typography>
              ) : (
                <Typography variant="body2" gutterBottom sx={{ maxWidth: { sm: '100%', md: '70%' } }}>
                  {item.a}
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Container>
  );
}
